import { useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

/**
 * Three terminal answers a teacher can give for a student who was present
 * earlier today but is now being re-marked Informiert ("excused") in the
 * attendance edit view:
 *
 *  - `left_school`           — Kid actually went home. Daily-log gets a
 *                              check_out_time; the per-lesson row writes
 *                              `left_early`. A `Begründung` is required
 *                              UNLESS an existing absence note already
 *                              explains why (Informiert/Krankgemeldet/
 *                              Beurlaubt covering this lesson).
 *  - `elsewhere_informiert`  — Kid is still in the building, just in a
 *                              different offer. Per-lesson row stays
 *                              `absent_excused` (= "Informiert" tag);
 *                              doesn't count as Fehlzeit on Zeugnis but
 *                              shows in the class-teacher Klärungen widget.
 *                              Reason ("where are they") is required.
 *  - `elsewhere_unclear`     — Teacher walked back the Informiert call:
 *                              kid is missing and no one knows where.
 *                              Per-lesson row flips to `absent_unclear`
 *                              (counts as Fehlzeit, surfaces in widget).
 *                              Reason is optional.
 */
export type EarlierPresentChoice =
  | "left_school"
  | "elsewhere_informiert"
  | "elsewhere_unclear";

export interface EarlierPresentDecision {
  studentId: string;
  choice: EarlierPresentChoice;
  /** Free text. Empty string when not applicable. */
  reason: string;
}

export interface EarlierPresentSaveDialogProps {
  open: boolean;
  students: Array<{ id: string; name: string }>;
  onCancel: () => void;
  onConfirm: (decisions: EarlierPresentDecision[]) => void;
  formatStudentName: (name: string) => string;
  /** Set of student IDs currently on Kurzbeschulung today. Used to render
   *  the "(KB)" badge + tooltip next to the student's name. */
  kbStudentIds?: Set<string>;
  /** Per-student KB time window in HH:mm form. Tooltip uses these to show
   *  "Kurzbeschulung <weekday>s: <start>-<end> Uhr". */
  kbStudentTimes?: Map<string, { start: string; end: string }>;
  /** Lesson date — used to derive the German weekday for the KB tooltip
   *  ("Kurzbeschulung dienstags: …"). ISO/timezoneless string is fine. */
  lessonStartDatetime?: string | null;
  /**
   * Student IDs that already have a covering absence note (Informiert,
   * Krankgemeldet, or Beurlaubt) for today / this lesson slot. For these
   * kids the Auschecken path doesn't require a Begründung — the note
   * already explains why they're leaving. Sonstiges still requires a
   * location (yellow) or allows an optional note (red).
   */
  studentIdsWithCoveringAbsenceNote?: Set<string>;
  /**
   * Per-student absence-note text (reason / decision_comment) so the row
   * can render the actual note instead of a generic "Abwesenheitsnotiz"
   * badge. Optional — falls back to the generic label when missing.
   */
  absenceNoteTextByStudent?: Map<string, string>;
}

/** German weekday name in plural-locative form ("dienstags"), used inside
 * KB tooltips. Falls back to the empty string for null/invalid dates. */
function germanWeekdayPlural(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  // 0 = Sun, 1 = Mon, …
  const map = [
    "sonntags",
    "montags",
    "dienstags",
    "mittwochs",
    "donnerstags",
    "freitags",
    "samstags",
  ];
  return map[d.getDay()] ?? "";
}

type RowState = {
  /** null = unanswered. */
  choice: EarlierPresentChoice | null;
  /** True when the teacher clicked Sonstiges but hasn't yet picked yellow
   *  or red. Lets us render the sub-choice row without committing to either
   *  branch. Reset whenever they pick a terminal choice or click Auschecken. */
  showSonstigesPanel: boolean;
  /** Free-text reason. Required for some choice/note combos (see
   *  `rowIsValid`). */
  reason: string;
};

function rowIsValid(row: RowState, hasNote: boolean): boolean {
  if (!row.choice) return false;
  if (row.choice === "left_school") {
    // Begründung only required when no existing absence note explains
    // why the kid is leaving.
    return hasNote ? true : !!row.reason.trim();
  }
  if (row.choice === "elsewhere_informiert") {
    // "Wo ist das Kind?" — always required.
    return !!row.reason.trim();
  }
  // elsewhere_unclear: optional reason.
  return true;
}

export function EarlierPresentSaveDialog({
  open,
  students,
  onCancel,
  onConfirm,
  formatStudentName,
  kbStudentIds,
  kbStudentTimes,
  lessonStartDatetime,
  studentIdsWithCoveringAbsenceNote,
  absenceNoteTextByStudent,
}: EarlierPresentSaveDialogProps) {
  const weekdayPlural = germanWeekdayPlural(lessonStartDatetime);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  // Reset row state every time the dialog opens with a new student set so
  // closing + reopening doesn't carry forward stale answers.
  useEffect(() => {
    if (!open) return;
    const init: Record<string, RowState> = {};
    students.forEach((s) => {
      init[s.id] = { choice: null, showSonstigesPanel: false, reason: "" };
    });
    setRows(init);
  }, [open, students]);

  const hasNoteFor = (sid: string): boolean =>
    !!studentIdsWithCoveringAbsenceNote?.has(sid);

  const setAll = (choice: EarlierPresentChoice) => {
    setRows((prev) => {
      const next: Record<string, RowState> = {};
      students.forEach((s) => {
        const existing = prev[s.id];
        next[s.id] = {
          choice,
          showSonstigesPanel: false,
          // Carry forward the existing reason ONLY if it's still applicable
          // to the new choice — otherwise clear so a stale "Anderswo: x"
          // doesn't end up tagged as an Auschecken reason.
          reason:
            existing?.choice === choice ? existing.reason : "",
        };
      });
      return next;
    });
  };

  const setRow = (studentId: string, patch: Partial<RowState>) => {
    setRows((prev) => {
      const cur = prev[studentId] || {
        choice: null,
        showSonstigesPanel: false,
        reason: "",
      };
      // If the new choice differs from the previous one, blank the reason
      // so we don't leak text between branches.
      const reasonReset =
        patch.choice !== undefined && patch.choice !== cur.choice;
      return {
        ...prev,
        [studentId]: {
          choice: patch.choice !== undefined ? patch.choice : cur.choice,
          showSonstigesPanel:
            patch.showSonstigesPanel !== undefined
              ? patch.showSonstigesPanel
              : cur.showSonstigesPanel,
          reason:
            patch.reason !== undefined
              ? patch.reason
              : reasonReset
                ? ""
                : cur.reason,
        },
      };
    });
  };

  const canConfirm = useMemo(() => {
    if (students.length === 0) return false;
    return students.every((s) => {
      const r = rows[s.id];
      if (!r) return false;
      return rowIsValid(r, hasNoteFor(s.id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, students, studentIdsWithCoveringAbsenceNote]);

  const handleConfirm = () => {
    if (!canConfirm) return;
    const decisions: EarlierPresentDecision[] = students.map((s) => {
      const r = rows[s.id];
      return {
        studentId: s.id,
        choice: r.choice as EarlierPresentChoice,
        reason: (r.reason || "").trim(),
      };
    });
    onConfirm(decisions);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Wo sind die Schüler*innen jetzt?</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Diese Schüler*innen sind immer noch eingecheckt (waren heute schon
            anwesend und wurden nicht ausgecheckt). Bitte checken Sie die
            Schüler*innen aus oder geben Sie an, falls diese in einem anderen
            Angebot (noch in der Schule) sind.
          </p>

          {students.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
              <span className="font-medium text-gray-700">Alle:</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAll("left_school")}
              >
                Auschecken
              </Button>
            </div>
          )}

          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {students.map((s) => {
              const row = rows[s.id] || {
                choice: null,
                showSonstigesPanel: false,
                reason: "",
              };
              const hasNote = hasNoteFor(s.id);
              const noteText = absenceNoteTextByStudent?.get(s.id) || "";
              const isKb = !!kbStudentIds?.has(s.id);
              const kbTimes = kbStudentTimes?.get(s.id);
              const kbTooltip =
                isKb && kbTimes
                  ? `Kurzbeschulung${weekdayPlural ? " " + weekdayPlural : ""}: ${kbTimes.start}-${kbTimes.end} Uhr`
                  : isKb
                    ? "Kurzbeschulung"
                    : undefined;

              // Top-row "Sonstiges" stays highlighted when EITHER the panel
              // is open OR a yellow/red sub-choice has been committed.
              const sonstigesActive =
                row.showSonstigesPanel ||
                row.choice === "elsewhere_informiert" ||
                row.choice === "elsewhere_unclear";

              return (
                <div
                  key={s.id}
                  className="rounded-md border border-gray-200 px-3 py-2"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="font-medium flex flex-col gap-1 min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span>{formatStudentName(s.name)}</span>
                        {isKb && (
                          <span
                            className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800 cursor-help shrink-0"
                            title={kbTooltip}
                            aria-label={kbTooltip}
                          >
                            KB
                          </span>
                        )}
                      </div>
                      {hasNote && (
                        <span
                          className="text-xs font-normal italic text-blue-800"
                          title="Bereits Abwesenheitsnotiz vorhanden"
                        >
                          {noteText
                            ? `„${noteText}“`
                            : "Abwesenheitsnotiz vorhanden"}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          row.choice === "left_school" ? "default" : "outline"
                        }
                        onClick={() =>
                          setRow(s.id, {
                            choice: "left_school",
                            showSonstigesPanel: false,
                          })
                        }
                      >
                        Auschecken
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={sonstigesActive ? "default" : "outline"}
                        onClick={() =>
                          setRow(s.id, {
                            // Clear any terminal sub-choice so the user
                            // re-picks yellow/red explicitly.
                            choice: null,
                            showSonstigesPanel: true,
                          })
                        }
                      >
                        Sonstiges
                      </Button>
                    </div>
                  </div>

                  {/* Sonstiges sub-choices */}
                  {sonstigesActive && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          row.choice === "elsewhere_informiert"
                            ? "default"
                            : "outline"
                        }
                        className={
                          row.choice === "elsewhere_informiert"
                            ? "bg-amber-500 hover:bg-amber-600 border-amber-500 text-white"
                            : "border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100"
                        }
                        onClick={() =>
                          setRow(s.id, {
                            choice: "elsewhere_informiert",
                            showSonstigesPanel: true,
                          })
                        }
                      >
                        Noch in der Schule
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          row.choice === "elsewhere_unclear"
                            ? "default"
                            : "outline"
                        }
                        className={
                          row.choice === "elsewhere_unclear"
                            ? "bg-red-600 hover:bg-red-700 border-red-600 text-white"
                            : "border-red-400 text-red-800 bg-red-50 hover:bg-red-100"
                        }
                        onClick={() =>
                          setRow(s.id, {
                            choice: "elsewhere_unclear",
                            showSonstigesPanel: true,
                          })
                        }
                      >
                        Doch ungeklärt
                      </Button>
                    </div>
                  )}

                  {/* Reason input */}
                  {row.choice === "left_school" && !hasNote && (
                    <div className="mt-2 space-y-1">
                      <label
                        htmlFor={`reason-${s.id}`}
                        className="block text-xs text-gray-700"
                      >
                        Warum wurde das Kind frühzeitig verlassen? Von wem
                        kommt die Information?
                      </label>
                      <Input
                        id={`reason-${s.id}`}
                        placeholder="Begründung"
                        value={row.reason}
                        onChange={(e) =>
                          setRow(s.id, { reason: e.target.value })
                        }
                      />
                    </div>
                  )}
                  {row.choice === "elsewhere_informiert" && (
                    <div className="mt-2 space-y-1">
                      <label
                        htmlFor={`reason-${s.id}`}
                        className="block text-xs text-gray-700"
                      >
                        Wo ist der/die Schüler*in? An welchem Angebot nimmt
                        der/die Schüler*in teil?
                      </label>
                      <Input
                        id={`reason-${s.id}`}
                        placeholder="z. B. Bibliothek, Sekretariat, AG Theater…"
                        value={row.reason}
                        onChange={(e) =>
                          setRow(s.id, { reason: e.target.value })
                        }
                      />
                    </div>
                  )}
                  {row.choice === "elsewhere_unclear" && (
                    <div className="mt-2 space-y-1">
                      <label
                        htmlFor={`reason-${s.id}`}
                        className="block text-xs text-red-700"
                      >
                        Der/die Schüler*in fehlt und wir wissen nicht warum
                        bzw. wo er/sie ist.
                      </label>
                      <Input
                        id={`reason-${s.id}`}
                        placeholder="Anmerkung (optional)"
                        value={row.reason}
                        onChange={(e) =>
                          setRow(s.id, { reason: e.target.value })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={!canConfirm}
            onClick={handleConfirm}
            className="bg-green-600 hover:bg-green-700"
          >
            Speichern
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
